/**
 * Price input cell component
 *
 * Custom cell for ag-grid that shows a price input.
 */
import { Component, Injector } from '@angular/core';
import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { get, merge } from 'lodash';

import {
  DEFAULT_CURRENCY_SYMBOL_POSITION,
  DEFAULT_NUMBER_PRECISION,
} from '@config/global-constants';
import { CountryFormatService, StateService } from '@shared/services';
import { XVIFloatInputRendererComponent } from '../float-input-renderer/float-input-renderer.component';
import { generateDecimalRegexp } from '@app/shared/constants';
import { generateMaximumByPreferencesPrecision } from '@app/shared/utils/number-utils';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-price-input-renderer',
  templateUrl: 'price-input-renderer.component.html',
  styleUrls: ['price-input-renderer.component.scss'],
})
export class XVIPriceInputRendererComponent
  extends XVIFloatInputRendererComponent
  implements AgRendererComponent
{
  public label: string;
  public step: number = 1;
  public symbolPosition: string = DEFAULT_CURRENCY_SYMBOL_POSITION;
  public keepValidZeros: number = 2;
  public minDecimalPlacesToCheck = DEFAULT_NUMBER_PRECISION;

  public constructor(
    public injector: Injector,
    public countryFormatService: CountryFormatService,
    public stateService: StateService,
  ) {
    super(injector, stateService);
  }

  public agInit(params: ICellRendererParams): void {
    this.precision = get(
      params,
      'precision',
      this.countryFormatService.getCurrencyPrecision(),
    );
    this.symbolPosition = get(
      params,
      'symbolPosition',
      this.countryFormatService.symbolPosition,
    );
    this.params = params;
    this.artificialInputCheck = get(
      params,
      'artificialInputCheck',
      this.artificialInputCheck,
    );
    this.pinnedRowValueMapper = get(
      params,
      'pinnedRowValueMapper',
      this.pinnedRowValueMapper,
    );
    this.placeholder = get(params, 'placeholder', '');
    this.disableKeyUpFinishEditing = get(
      params,
      'disableKeyUpFinishEditing',
      false,
    );
    this.acceptableInputRegex = get(
      params,
      'acceptableInputRegex',
      generateDecimalRegexp(this.stateService.getCurrencyPrecision()),
    );
    this.onValueChange = get(params, 'onValueChange', null);
    this.refresh(params);

    this.valueBeforeEdit = this.value;
    this.label = get(
      params,
      'label',
      this.countryFormatService.getCurrencySymbol(),
    );
    this.type = get(params, 'type', 'text');
    this.step = get(params, 'step', 1);
    this.class = get(params, 'class', '');
    this.disabled = get(this.params, 'disabled', false);
    this.usePreferenceDecimalPrecision = get(
      this.params,
      'usePreferenceDecimalPrecision',
      {
        overrideMin: true,
        overrideMax: true,
      },
    );
    const receivedMin = get(this.params, 'min', null);
    const receivedMax = get(this.params, 'max', null);
    this.min = this.usePreferenceDecimalPrecision.overrideMin
      ? generateMaximumByPreferencesPrecision(
          parseInt(String(receivedMin), 10),
          this.stateService.getCurrencyPrecision(),
        )
      : receivedMin;

    this.max = this.usePreferenceDecimalPrecision.overrideMax
      ? generateMaximumByPreferencesPrecision(
          parseInt(String(receivedMax), 10),
          this.stateService.getCurrencyPrecision(),
        )
      : receivedMax;

    this.emitItemAction = get(this.params, 'context.emitItemAction');
    this.lastEmittedValue = this.value;
  }

  protected getNumberValue(
    input: string | number,
    isInitStatus: boolean = false,
  ): number | string {
    const numberValue = super.getNumberValue(input, isInitStatus);

    if (get(this.params, 'disabled')) {
      return this.numberFormatService.getNumberFormatter(
        true,
        undefined,
        merge({}, this.numberFormatService.formatOpts, { symbol: '' }),
      )({
        value: input,
      });
    }

    return numberValue;
  }
}
